import { TranscriptionType } from '@app/modules/logger/screens/stream/components/audit/transcriptions/transcriptions.types';
import { formatVoiceLabel } from '@app/modules/logger/screens/stream/components/audit/transcriptions/utils';
import { DiarizationFormattedType } from '@app/modules/search/types';
import { DateTimeStr, Station, TimeRange, toDateTimeStr, toTimestamp } from '@mi-front/core';
import { useQuery } from '@tanstack/react-query';
import { theme } from '@ui/styles';
import { useMemo, useRef } from 'react';
import { apiClient } from '../../../api';

export const pastelColors = [
  '#FF9AA2', // pastel pink:
  '#7EAAC7', // pastel blue:
  '#66E2D3', // pastel green:
  '#D8B384', // pastel beige:
  '#A39EC9', // pastel purple:
  '#FF8C42', // pastel orange:
  '#70D6B3', // pastel mint:
  '#FF6F61', // pastel coral:
  '#9F94F0', // pastel lavender:
  '#FFA07A', // pastel peach:
];

export function useDiarization(
  station: Station,
  range: TimeRange<DateTimeStr>,
  diarizationEnabled = true,
) {
  const { data, refetch, isFetched } = useQuery({
    queryKey: [['transcriptions-markers', station.slug, range.from, range.to]],
    queryFn: () =>
      apiClient.segments
        .fetchTranscription(station.slug, range.from, range.to, diarizationEnabled)
        .then((res) => res),
    enabled: diarizationEnabled,
  });
  const speakers = useRef<{ speaker: string; color: string }[]>([]);

  const formatedData = useMemo(() => {
    const mappedData: DiarizationFormattedType[] =
      isFetched && diarizationEnabled
        ? data?.flat()?.map((transcription: TranscriptionType) => {
            return transcription?.diarization.length
              ? transcription?.diarization?.map((d) => {
                  if (
                    d.voice_label !== 'NO IDENTIFICADO' &&
                    d.voice_label !== 'SILENCE' &&
                    !speakers.current.find((item) => {
                      return formatVoiceLabel(item.speaker) === formatVoiceLabel(d.voice_label);
                    })
                  ) {
                    speakers.current = [
                      ...speakers.current,
                      {
                        speaker: formatVoiceLabel(d.voice_label),
                        color: pastelColors[speakers.current.length] ?? theme.aqua,
                      },
                    ];
                  }

                  return d.label !== 'SILENCE' && !d.voice_label.includes('NO IDENTIFICADO')
                    ? {
                        time: toDateTimeStr(toTimestamp(transcription.time) + d.segment.start),
                        distance: d.distance,
                        label: d.label,
                        segment: d.segment,
                        voice_label: d.voice_label,
                      }
                    : null;
                })
              : null;
          })
        : [];

    return diarizationEnabled ? mappedData?.flat().filter((data) => data) : [];
  }, [isFetched, data, speakers, diarizationEnabled]);

  return {
    data: formatedData,
    refetch: refetch,
    speakers: speakers,
    rawData: data,
  };
}
